import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
  Fade,
  Fab,
  Badge,
} from '@mui/material';
import {
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Image as ImageIcon,
  Timer as TimerIcon,
  Add as AddIcon,
  HelpOutline as HelpOutlineIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { RecipeStep, Recipe } from '../models/Recipe';
import MobileIngredientDrawer from './MobileIngredientDrawer';

interface RecipeStepPageProps {
  step: RecipeStep;
  stepIndex: number;
  totalSteps: number;
  isGeneratingImage: boolean;
  isGeneratingAudio: boolean;
  isPlayingAudio: boolean;
  onGenerateImage: () => void;
  onPlayAudio: () => void;
  onStartTimer?: (duration: number, label: string) => void;
  onOpenQuestionDialog?: () => void;
  isLeftPage?: boolean;
  recipe?: Recipe;
  isMobile?: boolean;
}

// Move timer patterns outside component to avoid recreating on every render
const TIMER_PATTERNS = [
  /(?:cook|bake|boil|simmer|steam|fry|sauté|roast|grill|broil|mix|whisk|knead|rest|chill|freeze|marinate|let sit)(?:\s+(?:for|until|about))?\s+(\d+(?:[-–]\d+)?)\s*(minutes?|mins?|hours?|hrs?|seconds?|secs?)/gi,
  /(\d+(?:[-–]\d+)?)\s*(minutes?|mins?|hours?|hrs?|seconds?|secs?)(?:\s+(?:until|more|longer|additional))?/gi,
];

// Extract timer suggestions function (moved outside component)
const extractTimerSuggestions = (content: string) => {
  const timers: Array<{ duration: number; label: string; displayTime: string }> = [];

  TIMER_PATTERNS.forEach(pattern => {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const timeValue = match[1];
        const timeUnit = match[2].toLowerCase();
        
        let minutes = 0;
        if (timeUnit.includes('hour') || timeUnit.includes('hr')) {
          minutes = parseInt(timeValue) * 60;
        } else if (timeUnit.includes('second') || timeUnit.includes('sec')) {
          minutes = Math.max(1, Math.round(parseInt(timeValue) / 60));
        } else {
          minutes = parseInt(timeValue);
        }

        if (timeValue.includes('-') || timeValue.includes('–')) {
          const [min, max] = timeValue.split(/[-–]/).map(n => parseInt(n.trim()));
          if (max) {
            if (timeUnit.includes('hour') || timeUnit.includes('hr')) {
              minutes = Math.round((min + max) / 2) * 60;
            } else if (timeUnit.includes('second') || timeUnit.includes('sec')) {
              minutes = Math.max(1, Math.round((min + max) / 2 / 60));
            } else {
              minutes = Math.round((min + max) / 2);
            }
          }
        }

        if (minutes > 0 && minutes <= 480) {
          timers.push({
            duration: minutes,
            label: `${match[0].trim()}`,
            displayTime: minutes >= 60 
              ? `${Math.floor(minutes / 60)}h ${minutes % 60}m`.replace(' 0m', '')
              : `${minutes}m`
          });
        }
      }
    });

    const uniqueTimers = timers.filter((timer, index, self) => 
      index === self.findIndex(t => t.duration === timer.duration)
    ).sort((a, b) => a.duration - b.duration);

    return uniqueTimers.slice(0, 3);
};

const RecipeStepPage: React.FC<RecipeStepPageProps> = ({
  step,
  stepIndex,
  totalSteps,
  isGeneratingImage,
  isGeneratingAudio,
  isPlayingAudio,
  onGenerateImage,
  onPlayAudio,
  onStartTimer,
  onOpenQuestionDialog,
  isLeftPage = false,
  recipe,
  isMobile: isMobileProp,
}) => {
  const theme = useTheme();
  const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = isMobileProp ?? isMobileBreakpoint;
  const [imageError, setImageError] = useState(false);
  const [ingredientDrawerOpen, setIngredientDrawerOpen] = useState(false);

  // Memoize timer suggestions to avoid expensive regex on every render
  const timerSuggestions = useMemo(
    () => extractTimerSuggestions(step.content),
    [step.content]
  );

  // Page flip animation variants
  const pageVariants = {
    enter: (isLeftPage: boolean) => ({
      rotateY: isLeftPage ? -90 : 90,
      opacity: 0,
      transformOrigin: isLeftPage ? 'right center' : 'left center',
    }),
    center: {
      rotateY: 0,
      opacity: 1,
      transformOrigin: isLeftPage ? 'right center' : 'left center',
    },
    exit: (isLeftPage: boolean) => ({
      rotateY: isLeftPage ? 90 : -90,
      opacity: 0,
      transformOrigin: isLeftPage ? 'right center' : 'left center',
    }),
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={stepIndex}
        custom={isLeftPage}
        variants={pageVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 20,
        }}
        style={{
          height: '100%',
          transformStyle: 'preserve-3d',
          perspective: '1200px',
        } as React.CSSProperties}
      >
        <Box
          sx={{
            height: '100%',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #2c2416 0%, #1a1611 100%)'
              : 'linear-gradient(135deg, #fdf6e3 0%, #f8f1dc 100%)',
            borderRadius: isMobile ? 2 : 0,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(139, 90, 43, 0.3)' : 'rgba(139, 90, 43, 0.2)'}`,
            boxShadow: theme.palette.mode === 'dark'
              ? 'inset 0 2px 4px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.3)'
              : 'inset 0 2px 4px rgba(0,0,0,0.05), 0 4px 8px rgba(0,0,0,0.1)',
            position: 'relative',
            p: { xs: 3, md: 3, lg: 4 }, // Responsive padding for better reflow
            display: 'flex',
            flexDirection: 'column',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: isLeftPage ? 'auto' : 0,
              right: isLeftPage ? 0 : 'auto',
              width: '30px',
              height: '100%',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, transparent 100%)'
                : 'linear-gradient(90deg, rgba(0,0,0,0.05) 0%, transparent 100%)',
              pointerEvents: 'none',
            },
          }}
        >
          {/* Page Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            mb: 3,
            pb: 2,
            borderBottom: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(139, 90, 43, 0.2)' : 'rgba(139, 90, 43, 0.15)'}`,
          }}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: '"Caveat", cursive',
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.6)' : 'rgba(139, 90, 43, 0.6)',
                  fontSize: '1rem',
                  mb: 0.5,
                }}
              >
                Step {stepIndex + 1} of {totalSteps}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontWeight: 700,
                  color: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
                  fontSize: isMobile ? '1.5rem' : '2rem',
                  fontStyle: 'italic',
                }}
              >
                Instructions
              </Typography>
            </Box>
            
            {/* Audio Control */}
            <Button
              onClick={onPlayAudio}
              disabled={isGeneratingAudio}
              startIcon={
                isGeneratingAudio ? (
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <Box 
                      component="img" 
                      src="/assets/images/otto/otto_audio.svg" 
                      alt="Generating Audio"
                      sx={{ width: 24, height: 24 }}
                    />
                  </motion.div>
                ) : isPlayingAudio ? (
                  <VolumeOffIcon />
                ) : (
                  <VolumeUpIcon />
                )
              }
              sx={{
                color: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 138, 101, 0.1)' 
                  : 'rgba(216, 67, 21, 0.1)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 138, 101, 0.2)'
                    : 'rgba(216, 67, 21, 0.2)',
                },
                minWidth: 'auto',
                px: 2,
              }}
            >
              Read Aloud
            </Button>
          </Box>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Step Image */}
            {(step.image || !imageError) && (
              <Fade in timeout={800}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `3px solid ${theme.palette.mode === 'dark' ? 'rgba(139, 90, 43, 0.3)' : 'rgba(139, 90, 43, 0.2)'}`,
                    backgroundColor: 'transparent',
                    maxHeight: isMobile ? 250 : 350,
                  }}
                >
                  {step.image && !imageError ? (
                    <img
                      src={step.image}
                      alt={`Step ${stepIndex + 1}`}
                      loading="lazy"
                      style={{
                        width: '100%',
                        objectFit: 'cover',
                        maxHeight: isMobile ? 250 : 350,
                        filter: theme.palette.mode === 'dark' ? 'brightness(0.9)' : 'none',
                      }}
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: isMobile ? 200 : 300,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(255, 248, 240, 0.02)'
                          : 'rgba(139, 90, 43, 0.05)',
                      }}
                    >
                      {isGeneratingImage ? (
                        <motion.div
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1.05 }}
                          transition={{ duration: 1, repeat: Infinity, repeatType: 'reverse' }}
                          style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' } as React.CSSProperties}
                        >
                          <Box
                            component="img"
                            src="/assets/images/otto/otto_paint.svg"
                            alt="Painting"
                            sx={{ width: 100, height: 100, mb: 1 }}
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: '"Caveat", cursive',
                              fontSize: '1.2rem',
                              color: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.6)' : 'rgba(139, 90, 43, 0.6)',
                            }}
                          >
                            Illustrating...
                          </Typography>
                        </motion.div>
                      ) : (
                        <Button
                          variant="text"
                          onClick={onGenerateImage}
                          startIcon={<ImageIcon />}
                          sx={{
                            fontFamily: '"Caveat", cursive',
                            fontSize: '1.1rem',
                            color: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
                            textTransform: 'none',
                          }}
                        >
                          Add Illustration
                        </Button>
                      )}
                    </Box>
                  )}
                </Paper>
              </Fade>
            )}

            {/* Step Instructions */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="body1" // Uses updated theme body1 (1.125rem)
                sx={{
                  fontSize: { xs: '1.25rem', lg: '1.5rem' }, // Responsive font size
                  lineHeight: 1.8,
                  color: 'text.primary',
                  textAlign: 'left', // Improved readability
                  mb: 3,
                  '&::first-letter': {
                    fontSize: '3em',
                    fontWeight: 700,
                    float: 'left',
                    lineHeight: 0.8,
                    margin: '0.05em 0.1em 0 0',
                    fontFamily: '"Playfair Display", Georgia, serif',
                    color: 'primary.main',
                  },
                }}
              >
                {step.content}
              </Typography>

              {/* Timer Suggestions */}
              {timerSuggestions.length > 0 && onStartTimer && (
                <Box 
                  sx={{ 
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 138, 101, 0.05)'
                      : 'rgba(216, 67, 21, 0.05)',
                    border: `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(255, 138, 101, 0.3)' : 'rgba(216, 67, 21, 0.3)'}`,
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 1.5, 
                      fontFamily: '"Caveat", cursive',
                      fontSize: '1.1rem',
                      color: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <TimerIcon sx={{ fontSize: 18 }} />
                    Set a timer:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {timerSuggestions.map((timer, index) => (
                      <Chip
                        key={index}
                        icon={<AddIcon sx={{ fontSize: 14 }} />}
                        label={timer.displayTime}
                        onClick={() => onStartTimer(timer.duration, timer.label)}
                        clickable
                        size="small"
                        sx={{
                          backgroundColor: 'transparent',
                          color: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 138, 101, 0.5)' : 'rgba(216, 67, 21, 0.5)'}`,
                          fontFamily: '"Crimson Text", serif',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? 'rgba(255, 138, 101, 0.1)'
                              : 'rgba(216, 67, 21, 0.1)',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Ask Otto Button - Prominent Help */}
              {onOpenQuestionDialog && (
                <Box sx={{ mt: 4 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<HelpOutlineIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenQuestionDialog?.();
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                    }}
                    sx={{
                      fontSize: '1.2rem',
                      py: 2,
                      backgroundColor: 'secondary.main', // Distinct from primary
                      color: 'white',
                      boxShadow: 3,
                    }}
                  >
                    Ask Otto for Help
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

        </Box>
      </motion.div>

      {/* Mobile Ingredient Drawer - Floating Button */}
      {isMobile && recipe && (
        <>
          <Fab
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIngredientDrawerOpen(true)}
            sx={{
              position: 'fixed',
              bottom: { xs: 80, sm: 90 },
              right: 16,
              zIndex: 1200,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #FF8A65 0%, #FF6B35 100%)'
                : 'linear-gradient(135deg, #FF6B35 0%, #d84315 100%)',
              color: 'white',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(255, 138, 101, 0.4)'
                : '0 4px 20px rgba(216, 67, 21, 0.3)',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #FF9A75 0%, #FF7B45 100%)'
                  : 'linear-gradient(135deg, #FF7B45 0%, #e84315 100%)',
              },
            }}
          >
            <Badge
              badgeContent={recipe.ingredients.split('\n').filter(i => i.trim()).length}
              color="secondary"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#6B9B7B' : '#4A7C59',
                  color: 'white',
                  fontWeight: 600,
                },
              }}
            >
              <MenuBookIcon />
            </Badge>
          </Fab>

          <MobileIngredientDrawer
            open={ingredientDrawerOpen}
            onClose={() => setIngredientDrawerOpen(false)}
            ingredients={recipe.ingredients}
          />
        </>
      )}
    </AnimatePresence>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(RecipeStepPage, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these props change
  return (
    prevProps.step.content === nextProps.step.content &&
    prevProps.step.image === nextProps.step.image &&
    prevProps.step.audio === nextProps.step.audio &&
    prevProps.stepIndex === nextProps.stepIndex &&
    prevProps.isGeneratingImage === nextProps.isGeneratingImage &&
    prevProps.isGeneratingAudio === nextProps.isGeneratingAudio &&
    prevProps.isPlayingAudio === nextProps.isPlayingAudio &&
    prevProps.recipe?.id === nextProps.recipe?.id &&
    prevProps.isMobile === nextProps.isMobile
  );
});
